import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import './AdminRewardSystem.css';

const AdminRewardSystem = () => {
  const navigate = useNavigate();
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('champion'); // 'champion' or 'regular'
  
  // Champion's Reward state
  const [championRewardName, setChampionRewardName] = useState('');
  const [championRewardDescription, setChampionRewardDescription] = useState('');
  const [championRewardImage, setChampionRewardImage] = useState('');
  const [championRewardItems, setChampionRewardItems] = useState([{ name: '', description: '', image: '' }]);
  const [onePerPerson, setOnePerPerson] = useState(true);
  const [selectedChampionPlayers, setSelectedChampionPlayers] = useState([]);
  
  // Regular Reward state
  const [regularRewardName, setRegularRewardName] = useState('');
  const [regularRewardDescription, setRegularRewardDescription] = useState('');
  const [regularRewardImage, setRegularRewardImage] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Wheel of Fortune state
  const [wheelRewardName, setWheelRewardName] = useState('');
  const [wheelRewardDescription, setWheelRewardDescription] = useState('');
  const [wheelRewardImage, setWheelRewardImage] = useState('');
  const [wheelRewardItems, setWheelRewardItems] = useState([{ name: '', description: '', image: '', probability: 10, quantity: 1 }]);
  const [wheelSpinCount, setWheelSpinCount] = useState(1);
  const [selectedWheelUsers, setSelectedWheelUsers] = useState([]); // Array of {userId, spinCount} // Array of {userId, spinCount}
  
  // Player selection modal state
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [playerModalType, setPlayerModalType] = useState('regular'); // 'champion', 'regular', or 'wheel'
  const [allPlayers, setAllPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  
  // Edit reward state
  const [editingReward, setEditingReward] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRewardName, setEditRewardName] = useState('');
  const [editRewardDescription, setEditRewardDescription] = useState('');
  const [editRewardImage, setEditRewardImage] = useState('');
  const [editRewardItems, setEditRewardItems] = useState([]);
  const [editSelectedPlayers, setEditSelectedPlayers] = useState([]);
  const [editSelectedUsers, setEditSelectedUsers] = useState([]);

  useEffect(() => {
    loadRewards();
    loadAllPlayers();
  }, []);

  const loadRewards = async () => {
    try {
      const q = query(collection(db, 'rewards'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const rewardList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRewards(rewardList);
    } catch (error) {
      console.error('Error loading rewards:', error);
      toast.error('Failed to load rewards');
    }
  };

  const loadAllPlayers = async () => {
    setLoadingPlayers(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const playerList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        pseudo: doc.data().pseudo || 'Unknown',
        email: doc.data().email || '',
        rank: doc.data().rank || 999,
        division: doc.data().division || 'Slifer Red'
      }));
      // Sort by rank
      playerList.sort((a, b) => a.rank - b.rank);
      setAllPlayers(playerList);
    } catch (error) {
      console.error('Error loading players:', error);
      toast.error('Failed to load players');
    } finally {
      setLoadingPlayers(false);
    }
  };

  const handleAddChampionReward = async () => {
    if (!championRewardName.trim() || !championRewardDescription.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    const validItems = championRewardItems.filter(item => item.name?.trim());
    if (validItems.length === 0) {
      toast.error('Please add at least one reward item with a name');
      return;
    }

    if (selectedChampionPlayers.length === 0) {
      toast.error('Please select at least one player for this reward');
      return;
    }

    // Sort selected players by rank
    const sortedPlayers = selectedChampionPlayers.slice().sort((a, b) => a.rank - b.rank);

    setLoading(true);
    try {
      await addDoc(collection(db, 'rewards'), {
        type: 'champion',
        name: championRewardName.trim(),
        description: championRewardDescription.trim(),
        imageUrl: championRewardImage.trim() || '/default-reward.png',
        items: validItems, // Array of { name, description, image }
        onePerPerson: onePerPerson,
        claimedBy: [], // Array of { userId, pseudo, rank, itemIndex, claimedAt }
        eligibleUsers: selectedChampionPlayers.map(p => p.id), // Selected player IDs
        eligiblePlayersInfo: sortedPlayers, // Store player info with ranks for reference
        currentClaimRank: sortedPlayers[0].rank, // Start with lowest rank (best rank)
        active: true,
        createdAt: serverTimestamp()
      });

      toast.success('✅ Champion reward created successfully!');
      setChampionRewardName('');
      setChampionRewardDescription('');
      setChampionRewardImage('');
      setChampionRewardItems([{ name: '', description: '', image: '' }]);
      setOnePerPerson(true);
      setSelectedChampionPlayers([]);
      loadRewards();
    } catch (error) {
      console.error('Error creating champion reward:', error);
      toast.error('Failed to create champion reward');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRegularReward = async () => {
    if (!regularRewardName.trim() || !regularRewardDescription.trim() || selectedUsers.length === 0) {
      toast.error('Please fill in all required fields and select at least one player');
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, 'rewards'), {
        type: 'regular',
        name: regularRewardName.trim(),
        description: regularRewardDescription.trim(),
        imageUrl: regularRewardImage.trim() || '/default-reward.png',
        eligibleUsers: selectedUsers, // Array of user IDs who can claim
        claimedBy: [], // Array of { userId, pseudo, claimedAt }
        active: true,
        createdAt: serverTimestamp()
      });

      toast.success('✅ Regular reward created successfully!');
      setRegularRewardName('');
      setRegularRewardDescription('');
      setRegularRewardImage('');
      setSelectedUsers([]);
      loadRewards();
    } catch (error) {
      console.error('Error creating regular reward:', error);
      toast.error('Failed to create regular reward');
    } finally {
      setLoading(false);
    }
  };

  const handleAddWheelFortuneReward = async () => {
    if (!wheelRewardName.trim() || !wheelRewardDescription.trim() || selectedWheelUsers.length === 0) {
      toast.error('Please fill in all required fields and select at least one player');
      return;
    }

    const validItems = wheelRewardItems.filter(item => item.name?.trim());
    if (validItems.length === 0) {
      toast.error('Please add at least one wheel item with a name');
      return;
    }

    setLoading(true);
    try {
      // Get eligible players info with their ranks for spin order
      const eligiblePlayersInfo = allPlayers
        .filter(p => selectedWheelUsers.some(u => u.userId === p.id))
        .map(p => ({
          id: p.id,
          pseudo: p.pseudo,
          rank: p.rank
        }))
        .sort((a, b) => a.rank - b.rank);

      // Create spinCount object mapping userId to their allowed spins
      const spinCountPerPlayer = {};
      selectedWheelUsers.forEach(user => {
        spinCountPerPlayer[user.userId] = user.spinCount;
      });

      await addDoc(collection(db, 'rewards'), {
        type: 'wheelFortune',
        name: wheelRewardName.trim(),
        description: wheelRewardDescription.trim(),
        imageUrl: wheelRewardImage.trim() || 'https://i.imgur.com/F1dq7sr.png',
        items: validItems, // Array of { name, description, image, probability, quantity }
        spinCount: spinCountPerPlayer, // Object { userId: numberOfSpins }
        eligibleUsers: selectedWheelUsers.map(u => u.userId), // Array of user IDs who can spin
        eligiblePlayersInfo, // Array of {id, pseudo, rank} for order
        spinsUsed: {}, // Object { userId: spinsCount }
        wonItems: {}, // Object { itemName: wonCount }
        winHistory: [], // Array of { userId, pseudo, itemName, wonAt, rank }
        active: true,
        createdAt: serverTimestamp()
      });

      toast.success('✅ Wheel of Fortune reward created successfully!');
      setWheelRewardName('');
      setWheelRewardDescription('');
      setWheelRewardImage('');
      setWheelRewardItems([{ name: '', description: '', image: '', probability: 10, quantity: 1 }]);
      setWheelSpinCount(1);
      setSelectedWheelUsers([]);
      loadRewards();
    } catch (error) {
      console.error('Error creating wheel fortune reward:', error);
      toast.error('Failed to create wheel fortune reward');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReward = async (rewardId, rewardName, isLocked) => {
    if (isLocked) {
      toast.error('🔒 This reward is locked. Unlock it first to delete.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete reward "${rewardName}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'rewards', rewardId));
      toast.success(`✅ Reward "${rewardName}" deleted`);
      loadRewards();
    } catch (error) {
      console.error('Error deleting reward:', error);
      toast.error('Failed to delete reward');
    }
  };

  const handleToggleLock = async (rewardId, currentLockStatus) => {
    try {
      await updateDoc(doc(db, 'rewards', rewardId), {
        isLocked: !currentLockStatus
      });
      toast.success(`✅ Reward ${!currentLockStatus ? 'locked' : 'unlocked'}`);
      loadRewards();
    } catch (error) {
      console.error('Error toggling lock:', error);
      toast.error('Failed to toggle lock');
    }
  };

  const handleToggleActive = async (rewardId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'rewards', rewardId), {
        active: !currentStatus
      });
      toast.success(`✅ Reward ${!currentStatus ? 'activated' : 'deactivated'}`);
      loadRewards();
    } catch (error) {
      console.error('Error toggling reward status:', error);
      toast.error('Failed to update reward status');
    }
  };

  const handleResetChampionReward = async (rewardId, rewardName, isLocked) => {
    if (isLocked) {
      toast.error('🔒 This reward is locked. Unlock it first to reset.');
      return;
    }

    if (!window.confirm(`Reset all claims for "${rewardName}"? This will allow users to claim again.`)) {
      return;
    }

    try {
      await updateDoc(doc(db, 'rewards', rewardId), {
        claimedBy: [],
        currentClaimRank: 1
      });
      toast.success(`✅ Reward "${rewardName}" reset successfully`);
      loadRewards();
    } catch (error) {
      console.error('Error resetting reward:', error);
      toast.error('Failed to reset reward');
    }
  };

  const handleResetWheelFortuneReward = async (rewardId, rewardName, isLocked) => {
    if (isLocked) {
      toast.error('🔒 This reward is locked. Unlock it first to reset.');
      return;
    }

    if (!window.confirm(`Reset all spins for "${rewardName}"? This will allow users to spin again.`)) {
      return;
    }

    try {
      await updateDoc(doc(db, 'rewards', rewardId), {
        spinsUsed: {},
        winHistory: []
      });
      toast.success(`✅ Wheel "${rewardName}" reset successfully`);
      loadRewards();
    } catch (error) {
      console.error('Error resetting wheel:', error);
      toast.error('Failed to reset wheel');
    }
  };

  const handleEditReward = (reward) => {
    setEditingReward(reward);
    setEditRewardName(reward.name);
    setEditRewardDescription(reward.description);
    setEditRewardImage(reward.imageUrl || '');
    
    if (reward.type === 'champion') {
      setEditRewardItems(reward.items || []);
      setEditSelectedPlayers(reward.eligiblePlayersInfo || []);
      setPlayerModalType('champion');
    } else {
      setEditSelectedUsers(reward.eligibleUsers || []);
      setPlayerModalType('regular');
    }
    
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editRewardName.trim() || !editRewardDescription.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        name: editRewardName.trim(),
        description: editRewardDescription.trim(),
        imageUrl: editRewardImage.trim() || '/default-reward.png'
      };

      if (editingReward.type === 'champion') {
        const validItems = editRewardItems.filter(item => item.name?.trim());
        if (validItems.length === 0) {
          toast.error('Please add at least one reward item with a name');
          setLoading(false);
          return;
        }
        
        if (editSelectedPlayers.length === 0) {
          toast.error('Please select at least one player');
          setLoading(false);
          return;
        }

        const sortedPlayers = editSelectedPlayers.slice().sort((a, b) => a.rank - b.rank);
        
        updateData.items = validItems;
        updateData.eligibleUsers = editSelectedPlayers.map(p => p.id);
        updateData.eligiblePlayersInfo = sortedPlayers;
      } else {
        if (editSelectedUsers.length === 0) {
          toast.error('Please select at least one player');
          setLoading(false);
          return;
        }
        
        updateData.eligibleUsers = editSelectedUsers;
      }

      await updateDoc(doc(db, 'rewards', editingReward.id), updateData);
      
      toast.success('✅ Reward updated successfully!');
      setShowEditModal(false);
      setEditingReward(null);
      loadRewards();
    } catch (error) {
      console.error('Error updating reward:', error);
      toast.error('Failed to update reward');
    } finally {
      setLoading(false);
    }
  };

  const addEditRewardItem = () => {
    setEditRewardItems([...editRewardItems, { name: '', description: '', image: '' }]);
  };

  const updateEditRewardItem = (index, field, value) => {
    const newItems = [...editRewardItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setEditRewardItems(newItems);
  };

  const removeEditRewardItem = (index) => {
    const newItems = editRewardItems.filter((_, i) => i !== index);
    setEditRewardItems(newItems);
  };

  const toggleEditPlayerSelection = (playerId) => {
    if (editingReward?.type === 'champion') {
      setEditSelectedPlayers(prev => {
        const player = allPlayers.find(p => p.id === playerId);
        if (prev.find(p => p.id === playerId)) {
          return prev.filter(p => p.id !== playerId);
        } else {
          return [...prev, player];
        }
      });
    } else {
      setEditSelectedUsers(prev => {
        if (prev.includes(playerId)) {
          return prev.filter(id => id !== playerId);
        } else {
          return [...prev, playerId];
        }
      });
    }
  };

  const addChampionRewardItem = () => {
    setChampionRewardItems([...championRewardItems, { name: '', description: '', image: '' }]);
  };

  const updateChampionRewardItem = (index, field, value) => {
    const newItems = [...championRewardItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setChampionRewardItems(newItems);
  };

  const removeChampionRewardItem = (index) => {
    const newItems = championRewardItems.filter((_, i) => i !== index);
    setChampionRewardItems(newItems);
  };

  const addWheelRewardItem = () => {
    setWheelRewardItems([...wheelRewardItems, { name: '', description: '', image: '', probability: 10, quantity: 1 }]);
  };

  const updateWheelRewardItem = (index, field, value) => {
    const newItems = [...wheelRewardItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setWheelRewardItems(newItems);
  };

  const removeWheelRewardItem = (index) => {
    const newItems = wheelRewardItems.filter((_, i) => i !== index);
    setWheelRewardItems(newItems);
  };

  const togglePlayerSelection = (playerId) => {
    // If we're in edit mode, use edit functions
    if (showEditModal) {
      toggleEditPlayerSelection(playerId);
      return;
    }
    
    if (playerModalType === 'champion') {
      setSelectedChampionPlayers(prev => {
        const player = allPlayers.find(p => p.id === playerId);
        if (prev.find(p => p.id === playerId)) {
          return prev.filter(p => p.id !== playerId);
        } else {
          return [...prev, player];
        }
      });
    } else if (playerModalType === 'wheel') {
      setSelectedWheelUsers(prev => {
        const exists = prev.find(u => u.userId === playerId);
        if (exists) {
          return prev.filter(u => u.userId !== playerId);
        } else {
          return [...prev, { userId: playerId, spinCount: 1 }];
        }
      });
    } else {
      setSelectedUsers(prev => {
        if (prev.includes(playerId)) {
          return prev.filter(id => id !== playerId);
        } else {
          return [...prev, playerId];
        }
      });
    }
  };

  const selectAllPlayers = () => {
    if (showEditModal) {
      if (editingReward?.type === 'champion') {
        setEditSelectedPlayers(allPlayers);
      } else {
        setEditSelectedUsers(allPlayers.map(p => p.id));
      }
    } else {
      if (playerModalType === 'champion') {
        setSelectedChampionPlayers(allPlayers);
      } else if (playerModalType === 'wheel') {
        setSelectedWheelUsers(allPlayers.map(p => ({ userId: p.id, spinCount: 1 })));
      } else {
        setSelectedUsers(allPlayers.map(p => p.id));
      }
    }
  };

  const deselectAllPlayers = () => {
    if (showEditModal) {
      if (editingReward?.type === 'champion') {
        setEditSelectedPlayers([]);
      } else {
        setEditSelectedUsers([]);
      }
    } else {
      if (playerModalType === 'champion') {
        setSelectedChampionPlayers([]);
      } else if (playerModalType === 'wheel') {
        setSelectedWheelUsers([]);
      } else {
        setSelectedUsers([]);
      }
    }
  };

  const getSelectedPlayerNames = () => {
    if (showEditModal) {
      if (editingReward?.type === 'champion') {
        return editSelectedPlayers
          .map(p => `${p.pseudo} (Rank #${p.rank})`)
          .join(', ');
      } else {
        return allPlayers
          .filter(p => editSelectedUsers.includes(p.id))
          .map(p => p.pseudo)
          .join(', ');
      }
    }
    
    if (playerModalType === 'champion') {
      return selectedChampionPlayers
        .map(p => `${p.pseudo} (Rank #${p.rank})`)
        .join(', ');
    } else if (playerModalType === 'wheel') {
      return allPlayers
        .filter(p => selectedWheelUsers.some(u => u.userId === p.id))
        .map(p => {
          const userEntry = selectedWheelUsers.find(u => u.userId === p.id);
          const spinCount = userEntry?.spinCount || 1;
          return `${p.pseudo} (${spinCount} spin${spinCount > 1 ? 's' : ''})`;
        })
        .join(', ');
    } else {
      return allPlayers
        .filter(p => selectedUsers.includes(p.id))
        .map(p => p.pseudo)
        .join(', ');
    }
  };

  return (
    <div className="admin-reward-system">
      <div className="reward-header">
        <h1>🏆 Reward System Manager</h1>
        <button className="back-button" onClick={() => navigate('/admin-panelx-952504')}>
          ← Back to Admin
        </button>
      </div>

      <div className="reward-tabs">
        <button 
          className={`tab-button ${activeTab === 'champion' ? 'active' : ''}`}
          onClick={() => setActiveTab('champion')}
        >
          👑 Champion's Reward
        </button>
        <button 
          className={`tab-button ${activeTab === 'regular' ? 'active' : ''}`}
          onClick={() => setActiveTab('regular')}
        >
          🎁 Regular Reward
        </button>
        <button 
          className={`tab-button ${activeTab === 'wheel' ? 'active' : ''}`}
          onClick={() => setActiveTab('wheel')}
        >
          🎡 Wheel of Fortune
        </button>
      </div>

      {activeTab === 'champion' && (
        <div className="create-reward-section">
          <h2>Create Champion's Reward</h2>
          <p className="info-text">
            Champion's Rewards are seasonal rewards for top-ranked players (Blue Obelisk).
            Players claim in order: Rank 1 → Rank 2 → Rank 3 → Rank 4
          </p>
          
          <div className="form-group">
            <label className="reward-label">Reward Name *</label>
            <input
              className="reward-input"
              type="text"
              value={championRewardName}
              onChange={(e) => setChampionRewardName(e.target.value)}
              placeholder="e.g., January 2026 Champion Rewards"
            />
          </div>

          <div className="form-group">
            <label className="reward-label">Description *</label>
            <textarea
              className="reward-input"
              value={championRewardDescription}
              onChange={(e) => setChampionRewardDescription(e.target.value)}
              placeholder="Describe the reward and claiming rules..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="reward-label">Image URL (optional)</label>
            <input
              className="reward-input"
              type="text"
              value={championRewardImage}
              onChange={(e) => setChampionRewardImage(e.target.value)}
              placeholder="https://example.com/reward-image.png"
            />
          </div>

          <div className="form-group">
            <label className="reward-label">Reward Items *</label>
            {championRewardItems.map((item, index) => (
              <div key={index} className="reward-item-card">
                <div className="item-card-header">
                  <h4>Item {index + 1}</h4>
                  {championRewardItems.length > 1 && (
                    <button 
                      className="remove-item-btn"
                      onClick={() => removeChampionRewardItem(index)}
                      type="button"
                    >
                      ✕ Remove
                    </button>
                  )}
                </div>
                
                <div className="item-form-group">
                  <label className="item-label">Item Name *</label>
                  <input
                    className="reward-input"
                    type="text"
                    value={item.name || ''}
                    onChange={(e) => updateChampionRewardItem(index, 'name', e.target.value)}
                    placeholder={`Name for item ${index + 1}`}
                  />
                </div>

                <div className="item-form-group">
                  <label className="item-label">Item Description</label>
                  <textarea
                    className="reward-input"
                    value={item.description || ''}
                    onChange={(e) => updateChampionRewardItem(index, 'description', e.target.value)}
                    placeholder={`Describe item ${index + 1}...`}
                    rows={2}
                  />
                </div>

                <div className="item-form-group">
                  <label className="item-label">Item Image URL</label>
                  <input
                    className="reward-input"
                    type="text"
                    value={item.image || ''}
                    onChange={(e) => updateChampionRewardItem(index, 'image', e.target.value)}
                    placeholder="https://example.com/item-image.png"
                  />
                  {item.image && (
                    <div className="item-preview">
                      <img src={item.image} alt={item.name || 'Item'} />
                    </div>
                  )}
                </div>
              </div>
            ))}
            <button className="add-item-btn" onClick={addChampionRewardItem} type="button">
              + Add Item
            </button>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={onePerPerson}
                onChange={(e) => setOnePerPerson(e.target.checked)}
              />
              One reward per person (recommended)
            </label>
          </div>

          <div className="form-group">
            <label className="reward-label">Select Eligible Players *</label>
            <button 
              type="button"
              className="select-players-btn"
              onClick={() => {
                setPlayerModalType('champion');
                setShowPlayerModal(true);
              }}
            >
              👥 Select Players ({selectedChampionPlayers.length} selected)
            </button>
            {selectedChampionPlayers.length > 0 && (
              <div className="selected-players-preview">
                <strong>Selected (by rank order):</strong> {getSelectedPlayerNames()}
              </div>
            )}
          </div>

          <button 
            className="create-reward-btn"
            onClick={handleAddChampionReward}
            disabled={loading}
          >
            {loading ? 'Creating...' : '🏆 Create Champion Reward'}
          </button>
        </div>
      )}

      {activeTab === 'regular' && (
        <div className="create-reward-section">
          <h2>Create Regular Reward</h2>
          <p className="info-text">
            Regular Rewards can be claimed by selected users. All eligible users can claim at any time.
          </p>
          
          <div className="form-group">
            <label className="reward-label">Reward Name *</label>
            <input
              className="reward-input"
              type="text"
              value={regularRewardName}
              onChange={(e) => setRegularRewardName(e.target.value)}
              placeholder="e.g., Special Event Reward"
            />
          </div>

          <div className="form-group">
            <label className="reward-label">Description *</label>
            <textarea
              className="reward-input"
              value={regularRewardDescription}
              onChange={(e) => setRegularRewardDescription(e.target.value)}
              placeholder="Describe the reward..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="reward-label">Image URL (optional)</label>
            <input
              className="reward-input"
              type="text"
              value={regularRewardImage}
              onChange={(e) => setRegularRewardImage(e.target.value)}
              placeholder="https://example.com/reward-image.png"
            />
          </div>

          <div className="form-group">
            <label className="reward-label">Select Eligible Players *</label>
            <button 
              type="button"
              className="select-players-btn"
              onClick={() => setShowPlayerModal(true)}
            >
              👥 Select Players ({selectedUsers.length} selected)
            </button>
            {selectedUsers.length > 0 && (
              <div className="selected-players-preview">
                <strong>Selected:</strong> {getSelectedPlayerNames()}
              </div>
            )}
          </div>

          <button 
            className="create-reward-btn"
            onClick={handleAddRegularReward}
            disabled={loading}
          >
            {loading ? 'Creating...' : '🎁 Create Regular Reward'}
          </button>
        </div>
      )}

      {activeTab === 'wheel' && (
        <div className="create-reward-section">
          <h2>Create Wheel of Fortune</h2>
          <p className="info-text">
            Wheel of Fortune allows eligible players to spin a wheel and win random prizes.
            You can set how many times each player can spin.
          </p>
          
          <div className="form-group">
            <label className="reward-label">Wheel Name *</label>
            <input
              className="reward-input"
              type="text"
              value={wheelRewardName}
              onChange={(e) => setWheelRewardName(e.target.value)}
              placeholder="e.g., Lucky Spin Event"
            />
          </div>

          <div className="form-group">
            <label className="reward-label">Description *</label>
            <textarea
              className="reward-input"
              value={wheelRewardDescription}
              onChange={(e) => setWheelRewardDescription(e.target.value)}
              placeholder="Describe the wheel and prizes..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="reward-label">Image URL (optional)</label>
            <input
              className="reward-input"
              type="text"
              value={wheelRewardImage}
              onChange={(e) => setWheelRewardImage(e.target.value)}
              placeholder="https://example.com/wheel-image.png"
            />
          </div>

          <div className="form-group">
            <label className="reward-label">Number of Spins Per Player *</label>
            <input
              className="reward-input"
              type="number"
              min="1"
              max="100"
              value={wheelSpinCount}
              onChange={(e) => setWheelSpinCount(parseInt(e.target.value) || 1)}
              placeholder="1"
            />
          </div>

          <div className="form-group">
            <label className="reward-label">Wheel Items *</label>
            {wheelRewardItems.map((item, index) => (
              <div key={index} className="reward-item-card">
                <div className="item-card-header">
                  <h4>Item {index + 1}</h4>
                  {wheelRewardItems.length > 1 && (
                    <button 
                      className="remove-item-btn"
                      onClick={() => removeWheelRewardItem(index)}
                      type="button"
                    >
                      ✕ Remove
                    </button>
                  )}
                </div>
                
                <div className="item-inputs">
                  <input
                    type="text"
                    placeholder="Item name *"
                    value={item.name}
                    onChange={(e) => updateWheelRewardItem(index, 'name', e.target.value)}
                    className="reward-input"
                  />
                  <textarea
                    placeholder="Item description (optional)"
                    value={item.description}
                    onChange={(e) => updateWheelRewardItem(index, 'description', e.target.value)}
                    className="reward-input"
                    rows={2}
                  />
                  <input
                    type="text"
                    placeholder="Item image URL (optional)"
                    value={item.image}
                    onChange={(e) => updateWheelRewardItem(index, 'image', e.target.value)}
                    className="reward-input"
                  />
                  <div className="probability-input-group">
                    <label>Probability Weight (higher = more likely)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      placeholder="Weight (1-100)"
                      value={item.probability}
                      onChange={(e) => updateWheelRewardItem(index, 'probability', parseInt(e.target.value) || 10)}
                      className="reward-input"
                    />
                  </div>
                  <div className="probability-input-group">
                    <label>Quantity (how many can be won)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      placeholder="Quantity"
                      value={item.quantity}
                      onChange={(e) => updateWheelRewardItem(index, 'quantity', parseInt(e.target.value) || 1)}
                      className="reward-input"
                    />
                  </div>
                </div>
              </div>
            ))}
            <button 
              className="add-item-btn"
              onClick={addWheelRewardItem}
              type="button"
            >
              ➕ Add Item
            </button>
          </div>

          <div className="form-group">
            <label className="reward-label">Select Eligible Players *</label>
            <button 
              type="button"
              className="select-players-btn"
              onClick={() => {
                setPlayerModalType('wheel');
                setShowPlayerModal(true);
              }}
            >
              👥 Select Players ({selectedWheelUsers.length} selected)
            </button>
            {selectedWheelUsers.length > 0 && (
              <div className="selected-players-preview">
                <strong>Selected:</strong> {getSelectedPlayerNames()}
              </div>
            )}
          </div>

          <button 
            className="create-reward-btn"
            onClick={handleAddWheelFortuneReward}
            disabled={loading}
          >
            {loading ? 'Creating...' : '🎡 Create Wheel of Fortune'}
          </button>
        </div>
      )}

      <div className="rewards-list-section">
        <h2>Existing Rewards ({rewards.length})</h2>
        {rewards.length === 0 ? (
          <div className="empty-state">
            <p>No rewards yet. Create your first reward!</p>
          </div>
        ) : (
          <div className="rewards-list">
            {rewards.map((reward) => (
              <div key={reward.id} className={`reward-item ${!reward.active ? 'inactive' : ''} ${reward.isLocked ? 'locked' : ''}`}>
                <div className="reward-header-row">
                  <div className="reward-type-badge">
                    {reward.type === 'champion' ? '👑 Champion' : reward.type === 'wheelFortune' ? '🎡 Wheel of Fortune' : '🎁 Regular'}
                  </div>
                  <div className="reward-status">
                    {reward.isLocked && (
                      <span className="lock-badge">
                        🔒 Locked
                      </span>
                    )}
                    <span className={`status-badge ${reward.active ? 'active' : 'inactive'}`}>
                      {reward.active ? '✓ Active' : '✕ Inactive'}
                    </span>
                  </div>
                </div>
                
                <h3>{reward.name}</h3>
                <p className="reward-description">{reward.description}</p>

                {reward.type === 'champion' && (
                  <div className="reward-details">
                    <div className="detail-row">
                      <strong>Items:</strong> {reward.items?.length || 0}
                    </div>
                    <div className="detail-row">
                      <strong>Claims:</strong> {reward.claimedBy?.length || 0} / {reward.eligibleUsers?.length || 0}
                    </div>
                    <div className="detail-row">
                      <strong>Next Rank:</strong> #{reward.currentClaimRank || 1}
                    </div>
                  </div>
                )}

                {reward.type === 'regular' && (
                  <div className="reward-details">
                    <div className="detail-row">
                      <strong>Eligible Users:</strong> {reward.eligibleUsers?.length || 0}
                    </div>
                    <div className="detail-row">
                      <strong>Claimed:</strong> {reward.claimedBy?.length || 0}
                    </div>
                  </div>
                )}

                {reward.type === 'wheelFortune' && (
                  <div className="reward-details">
                    <div className="detail-row">
                      <strong>Wheel Items:</strong> {reward.items?.length || 0}
                    </div>
                    <div className="detail-row">
                      <strong>Eligible Users:</strong> {reward.eligibleUsers?.length || 0}
                    </div>
                    <div className="detail-row">
                      <strong>Spins Per Player:</strong> {
                        reward.spinCount && typeof reward.spinCount === 'object'
                          ? `Custom (${Object.values(reward.spinCount).join(', ')} spins)`
                          : (reward.spinCount || 1)
                      }
                    </div>
                    <div className="detail-row">
                      <strong>Total Wins:</strong> {reward.winHistory?.length || 0}
                    </div>
                  </div>
                )}

                {/* Claims Summary */}
                {reward.claimedBy && reward.claimedBy.length > 0 && reward.type !== 'wheelFortune' && (
                  <div className="claims-section">
                    <h4>📋 Claims Summary</h4>
                    <div className="claims-list">
                      {reward.claimedBy.map((claim, index) => (
                        <div key={index} className="claim-item">
                          <div className="claim-info">
                            <div className="claim-player">
                              <strong>{claim.pseudo}</strong>
                              {claim.rank && <span className="claim-rank">Rank #{claim.rank}</span>}
                            </div>
                            <div className="claim-item-name">
                              {claim.itemName || 'Regular Reward'}
                            </div>
                            <div className="claim-date">
                              {new Date(claim.claimedAt).toLocaleDateString()} {new Date(claim.claimedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Wheel Fortune Win History */}
                {reward.type === 'wheelFortune' && reward.winHistory && reward.winHistory.length > 0 && (
                  <div className="claims-section">
                    <h4>🎡 Win History</h4>
                    <div className="claims-list">
                      {reward.winHistory.map((win, index) => (
                        <div key={index} className="claim-item">
                          <div className="claim-info">
                            <div className="claim-player">
                              <strong>{win.pseudo}</strong>
                            </div>
                            <div className="claim-item-name">
                              Won: {win.itemName}
                            </div>
                            <div className="claim-date">
                              {new Date(win.wonAt).toLocaleDateString()} {new Date(win.wonAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="reward-actions">
                  <button 
                    onClick={() => handleToggleLock(reward.id, reward.isLocked)}
                    className={`lock-btn ${reward.isLocked ? 'locked' : 'unlocked'}`}
                    title={reward.isLocked ? 'Click to unlock' : 'Click to lock'}
                  >
                    {reward.isLocked ? '🔒 Unlock' : '🔓 Lock'}
                  </button>
                  <button 
                    onClick={() => handleEditReward(reward)}
                    className="edit-btn"
                  >
                    ✏️ Edit
                  </button>
                  <button 
                    onClick={() => handleToggleActive(reward.id, reward.active)}
                    className={`toggle-btn ${reward.active ? 'deactivate' : 'activate'}`}
                  >
                    {reward.active ? '⏸️ Deactivate' : '▶️ Activate'}
                  </button>
                  {reward.type === 'champion' && (
                    <button 
                      onClick={() => handleResetChampionReward(reward.id, reward.name, reward.isLocked)}
                      className={`reset-btn ${reward.isLocked ? 'disabled' : ''}`}
                      disabled={reward.isLocked}
                    >
                      🔄 Reset Claims
                    </button>
                  )}
                  {reward.type === 'wheelFortune' && (
                    <button 
                      onClick={() => handleResetWheelFortuneReward(reward.id, reward.name, reward.isLocked)}
                      className={`reset-btn ${reward.isLocked ? 'disabled' : ''}`}
                      disabled={reward.isLocked}
                    >
                      🔄 Reset Spins
                    </button>
                  )}
                  <button 
                    onClick={() => handleDeleteReward(reward.id, reward.name, reward.isLocked)}
                    className={`delete-btn ${reward.isLocked ? 'disabled' : ''}`}
                    disabled={reward.isLocked}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Reward Modal */}
      {showEditModal && editingReward && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✏️ Edit Reward</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label className="reward-label">Reward Name *</label>
                <input
                  className="reward-input"
                  type="text"
                  value={editRewardName}
                  onChange={(e) => setEditRewardName(e.target.value)}
                  placeholder="Reward name..."
                />
              </div>

              <div className="form-group">
                <label className="reward-label">Description *</label>
                <textarea
                  className="reward-input"
                  value={editRewardDescription}
                  onChange={(e) => setEditRewardDescription(e.target.value)}
                  placeholder="Describe the reward..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label className="reward-label">Image URL (optional)</label>
                <input
                  className="reward-input"
                  type="text"
                  value={editRewardImage}
                  onChange={(e) => setEditRewardImage(e.target.value)}
                  placeholder="https://example.com/reward-image.png"
                />
              </div>

              {editingReward.type === 'champion' && (
                <div className="form-group">
                  <label className="reward-label">Reward Items *</label>
                  {editRewardItems.map((item, index) => (
                    <div key={index} className="reward-item-card">
                      <div className="item-card-header">
                        <h4>Item {index + 1}</h4>
                        {editRewardItems.length > 1 && (
                          <button 
                            className="remove-item-btn"
                            onClick={() => removeEditRewardItem(index)}
                            type="button"
                          >
                            ✕ Remove
                          </button>
                        )}
                      </div>
                      
                      <div className="item-inputs">
                        <input
                          type="text"
                          placeholder="Item name *"
                          value={item.name}
                          onChange={(e) => updateEditRewardItem(index, 'name', e.target.value)}
                          className="reward-input"
                        />
                        <textarea
                          placeholder="Item description (optional)"
                          value={item.description}
                          onChange={(e) => updateEditRewardItem(index, 'description', e.target.value)}
                          className="reward-input"
                          rows={2}
                        />
                        <input
                          type="text"
                          placeholder="Item image URL (optional)"
                          value={item.image}
                          onChange={(e) => updateEditRewardItem(index, 'image', e.target.value)}
                          className="reward-input"
                        />
                      </div>
                    </div>
                  ))}
                  <button 
                    className="add-item-btn"
                    onClick={addEditRewardItem}
                    type="button"
                  >
                    + Add Another Item
                  </button>
                </div>
              )}

              <div className="form-group">
                <label className="reward-label">Select Eligible Players *</label>
                <button 
                  type="button"
                  className="select-players-btn"
                  onClick={() => setShowPlayerModal(true)}
                >
                  👥 Select Players ({editingReward.type === 'champion' ? editSelectedPlayers.length : editSelectedUsers.length} selected)
                </button>
                {((editingReward.type === 'champion' && editSelectedPlayers.length > 0) || 
                  (editingReward.type === 'regular' && editSelectedUsers.length > 0)) && (
                  <div className="selected-players-preview">
                    <strong>Selected:</strong> {getSelectedPlayerNames()}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="modal-cancel-btn" 
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button 
                className="modal-confirm-btn" 
                onClick={handleSaveEdit}
                disabled={loading}
              >
                {loading ? 'Saving...' : '💾 Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Player Selection Modal */}
      {showPlayerModal && (
        <div className="modal-overlay" onClick={() => setShowPlayerModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Select Players</h2>
              <button className="modal-close" onClick={() => setShowPlayerModal(false)}>✕</button>
            </div>
            
            <div className="modal-actions">
              <button className="select-all-btn" onClick={selectAllPlayers}>
                Select All
              </button>
              <button className="deselect-all-btn" onClick={deselectAllPlayers}>
                Deselect All
              </button>
              <span className="selection-count">
                {showEditModal 
                  ? (editingReward?.type === 'champion' ? editSelectedPlayers.length : editSelectedUsers.length)
                  : (playerModalType === 'champion' ? selectedChampionPlayers.length : 
                     playerModalType === 'wheel' ? selectedWheelUsers.length : selectedUsers.length)
                } selected
              </span>
            </div>

            <div className="modal-body">
              {loadingPlayers ? (
                <div className="loading-message">Loading players...</div>
              ) : allPlayers.length === 0 ? (
                <div className="empty-message">No players found</div>
              ) : (
                <div className="players-grid">
                  {allPlayers.map(player => {
                    const isSelected = showEditModal
                      ? (editingReward?.type === 'champion' 
                          ? editSelectedPlayers.some(p => p.id === player.id)
                          : editSelectedUsers.includes(player.id))
                      : (playerModalType === 'champion' 
                          ? selectedChampionPlayers.some(p => p.id === player.id)
                          : playerModalType === 'wheel'
                          ? selectedWheelUsers.some(u => u.userId === player.id)
                          : selectedUsers.includes(player.id));
                    
                    const wheelUser = playerModalType === 'wheel' && selectedWheelUsers.find(u => u.userId === player.id);
                    
                    return (
                    <div 
                      key={player.id}
                      className={`player-item ${isSelected ? 'selected' : ''}`}
                    >
                      <div 
                        className="player-checkbox"
                        onClick={() => togglePlayerSelection(player.id)}
                      >
                        {isSelected && '✓'}
                      </div>
                      <div 
                        className="player-info"
                        onClick={() => togglePlayerSelection(player.id)}
                      >
                        <div className="player-name">{player.pseudo}</div>
                        <div className="player-details">
                          Rank #{player.rank} • {player.division}
                        </div>
                      </div>
                      {wheelUser && (
                        <div className="spin-count-input-wrapper">
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={wheelUser.spinCount}
                            onChange={(e) => {
                              const newCount = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
                              setSelectedWheelUsers(prev => 
                                prev.map(u => u.userId === player.id ? { ...u, spinCount: newCount } : u)
                              );
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="spin-count-input"
                          />
                          <span className="spin-count-label">spins</span>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="modal-confirm-btn" 
                onClick={() => setShowPlayerModal(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRewardSystem;
